import React, { useEffect, useState } from "react";
import { Box, IconButton, Tooltip, TextField, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FaSearch, FaDownload } from "react-icons/fa";
import PageHeader1 from "../../components/common/PageHeader1";
import axios from "axios";
import toast from "react-hot-toast";

function CourseSellingReport() {
  const [rowsData, setRowsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationDetail, setPaginationDetail] = useState({
    totalRecords: 0,
  });

  // Search and date filter states
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  // Load data on mount and when pagination changes (only if search is not applied)
  useEffect(() => {
    // If search is applied, don't trigger this effect for pagination
    if (appliedSearch) {
      return;
    }

    fetchCourseSellingData();
  }, [paginationModel, appliedStartDate, appliedEndDate, appliedSearch]);

  const fetchCourseSellingData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;

      const params = { page, limit };

      // Add date filters if applied
      if (appliedStartDate) {
        params.startDate = appliedStartDate;
      }
      if (appliedEndDate) {
        params.endDate = appliedEndDate;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/courseSellingReport`,
        {
          params: params,
          headers: { Authorization: token },
        }
      );

      // Handle different response formats
      let data = [];
      if (Array.isArray(response.data)) {
        data = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (response.data?.success && Array.isArray(response.data.data)) {
        data = response.data.data;
      }

      if (data.length > 0 || response.data) {
        setRowsData(data.length > 0 ? data : (Array.isArray(response.data) ? response.data : []));
        setPaginationDetail({
          totalRecords: response.data?.paginationDetail?.totalRecords ||
            response.data?.totalRecords ||
            response.data?.total ||
            (data.length > 0 ? data.length : 0),
        });
      }
    } catch (error) {
      console.error("Error fetching course selling report:", error);
      toast.error(error.response?.data?.message || "Failed to fetch course selling report");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (!searchValue.trim()) {
      // If search is empty, reset and load all data
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      fetchCourseSellingData();
      return;
    }
    setAppliedSearch(searchValue);
    // Call API with only search parameter (no page/limit)
    const token = localStorage.getItem("vedhvidhi-admin-token");
    setLoading(true);
    axios.get(
      `${process.env.REACT_APP_BASEURL}/admin/courseSellingReport`,
      {
        params: { search: searchValue },
        headers: { Authorization: token },
      }
    ).then(response => {
      if (response.data?.success || response.data?.data) {
        setRowsData(response.data?.data || response.data || []);
        setPaginationDetail({
          totalRecords: response.data?.data?.length || 0,
        });
      }
    }).catch(error => {
      console.error("Error searching:", error);
      toast.error(error.response?.data?.message || "Failed to search");
    }).finally(() => {
      setLoading(false);
    });
  };

  // Handle when search input is cleared
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchValue(value);

    // If search is cleared (empty), reset and load all data
    if (!value.trim() && appliedSearch) {
      setAppliedSearch("");
      setPaginationModel({ page: 0, pageSize: 10 });
      fetchCourseSellingData();
    }
  };

  const handleApplyFilter = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start date and end date");
      return;
    }
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedSearch(""); // Clear search when applying date filter
    setPaginationModel({ page: 0, pageSize: 10 }); // Reset to first page when applying filter
    fetchCourseSellingData();
  };

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setSearchValue("");
    setAppliedStartDate("");
    setAppliedEndDate("");
    setAppliedSearch("");
    setPaginationModel({ page: 0, pageSize: 10 });
    // Reload without filters
    fetchCourseSellingData();
  };

  const handleDownloadInvoice = async (row) => {
    try {
      // If invoicePdfUrl exists, open it directly
      if (row.invoicePdfUrl) {
        window.open(row.invoicePdfUrl, "_blank", "noopener,noreferrer");
        toast.success("Invoice opened successfully!");
        return;
      }

      // Otherwise, download from API
      const token = localStorage.getItem("vedhvidhi-admin-token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/course-selling-report/invoice/${row.id}`,
        {
          headers: { Authorization: token },
          responseType: "blob",
        }
      );

      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${row.invoiceNumber || row.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error(error.response?.data?.message || "Failed to download invoice");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  };

  const columns = [
    { field: "sono", headerName: "S.No.", width: 80 },
    { field: "courseName", headerName: "Course Name", width: 200 },
    { field: "batchName", headerName: "Batch Name", width: 180 },
    { field: "username", headerName: "Username", width: 150 },
    { field: "studentName", headerName: "Student Name", width: 180 },
    { field: "studentEmail", headerName: "Student Email", width: 220 },
    {
      field: "studentDOB",
      headerName: "Student DOB",
      width: 150,

    },
    {
      field: "actualPrice",
      headerName: "Actual Price",
      width: 130,
      // valueGetter: (params) => {
      //   const price = params?.row?.actualPrice;
      //   return price ? `₹${parseFloat(price).toFixed(2)}` : "₹0.00";
      // },
    },
    {
      field: "discountApplied",
      headerName: "Discount Applied",
      width: 150,
      // valueGetter: (params) => {
      //   const discount = params?.row?.discountApplied;
      //   return discount ? `₹${parseFloat(discount).toFixed(2)}` : "₹0.00";
      // },
    },
    {
      field: "discountedPrice",
      headerName: "Discounted Price",
      width: 150,
      // valueGetter: (params) => {
      //   const price = params?.row?.discountedPrice;
      //   return price ? `₹${parseFloat(price).toFixed(2)}` : "₹0.00";
      // },
    },
    {
      field: "createdDate",
      headerName: "Created Date",
      width: 180,
      valueGetter: (params) => {
        // const date = params?.row?.createdAt || params?.row?.createdDate || params?.row?.purchasedDate;
        const date = params;
        if (!date) return "N/A";
        try {
          return formatDateTime(date);
        } catch (error) {
          return date;
        }
      },
    },
    {
      field: "actions",
      headerName: "Action",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Download Invoice">
          <IconButton
            onClick={() => handleDownloadInvoice(params.row)}
            color="primary"
            size="small"
          >
            <FaDownload />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Calculate rowCount: use paginationDetail if available, otherwise use data length (for search)
  const rowCount = appliedSearch
    ? (rowsData?.length || 0)
    : (paginationDetail?.totalRecords || 0);

  const rows = (rowsData || []).map((item, index) => {
    // Ensure all fields are properly mapped from API response
    return {
      id: item._id || item.id || `row-${index}`,
      sono: paginationModel.page * paginationModel.pageSize + index + 1,
      courseName: item.courseName || "N/A",
      batchName: item.batchName || "N/A",
      username: item.username || "N/A",
      studentName: item.studentName || "N/A",
      studentEmail: item.studentEmail || "N/A",
      studentDOB: item.studentDOB || "",
      actualPrice: item.actualPrice || "0.00",
      discountApplied: item.discountApplied || "0.00",
      discountedPrice: item.discountedPrice || "0.00",
      // Map createdAt to createdDate for the column
      createdDate: item.createdAt || item.createdDate || item.purchasedDate || "",
      // Keep original fields for valueGetter access
      createdAt: item.createdAt || item.purchasedDate || "",
      purchasedDate: item.purchasedDate || item.createdAt || "",
      invoicePdfUrl: item.invoicePdfUrl || "",
      invoiceNumber: item.invoiceNumber || "",
      transactionId: item.transactionId || "",
      couponCode: item.couponCode || null,
    };
  });

  return (
    <div className="body d-flex py-1">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Course Selling Report" />
        <div className="row g-0 mb-3">
          <div className="col-md-12">
            <div className="card">
              <div className="card-body">
                <Box sx={{ width: "100%" }}>
                  {/* Search and Date Filter Section - Inside table card, compact format */}
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end" gap={2} mb={2} flexWrap="wrap">
                    {/* Search on left */}
                    <Box display="flex" alignItems="flex-end" gap={1}>
                      <TextField
                        placeholder="Search..."
                        value={searchValue}
                        onChange={handleSearchChange}
                        size="small"
                        sx={{ width: '250px' }}
                      />
                      <IconButton
                        onClick={handleSearch}
                        disabled={loading || !searchValue.trim()}
                        sx={{
                          height: '40px',
                          width: '40px',
                          backgroundColor: 'primary.main',
                          color: 'white',
                          borderRadius: '4px',
                          '&:hover': {
                            backgroundColor: 'primary.dark',
                          },
                          '&:disabled': {
                            backgroundColor: 'action.disabledBackground',
                            color: 'action.disabled',
                          }
                        }}
                      >
                        <FaSearch />
                      </IconButton>
                    </Box>

                    {/* Date filters on right */}
                    <Box display="flex" alignItems="flex-end" gap={2} flexWrap="wrap">
                      <TextField
                        type="date"
                        label="Start Date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: '180px' }}
                      />
                      <TextField
                        type="date"
                        label="End Date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        sx={{ width: '180px' }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleApplyFilter}
                        disabled={loading}
                        sx={{ height: '40px' }}
                      >
                        {loading ? "Loading..." : "Apply"}
                      </Button>
                      <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleClearFilters}
                        sx={{ height: '40px' }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ height: 600 }}>
                    <DataGrid
                      columns={columns}
                      rows={rows}
                      rowCount={rowCount}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      pageSizeOptions={[10, 25, 50]}
                      paginationMode={appliedSearch ? "client" : "server"}
                      disableSelectionOnClick
                      loading={loading}
                      getRowId={(row) => row.id}
                    />
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseSellingReport;

